import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import {
    useActions,
    useIsCaseworker,
    useIsPartner,
    useTranslation,
    useTypedSelector,
} from '../../../../hooks'
import { Card } from '../../../styled/cards'
import { HeadingSecondary } from '../../../styled/typography'
import MyAccount from './frames/MyAccount'
import Payment from './frames/Payment'
import Organization from './frames/Organization'
import AutoReply from './frames/AutoReply'
import PartnerUsers from './frames/PartnerUsers'

type CurrentFrameNameType =
    | 'my-account'
    | 'organization'
    | 'users'
    | 'payment-details'
    | 'payment-methods'
    | 'auto-reply'

const Container = styled.div`
    width: 100%;
    padding: 4rem;
    position: relative;
`

const FlexContainer = styled.div`
    display: flex;
    gap: 2.2rem;
    padding-top: 2rem;
`

const LeftContainer = styled.div`
    width: 25%;
`

const RightContainer = styled.div`
    width: 75%;
`

const NavigationContainer = styled.div`
    height: 46rem;
    position: relative;
`

interface IsActive {
    isActive: boolean
}

const NavigationLink = styled.div<IsActive>`
    height: 6.2rem;
    width: 100%;
    border-bottom: ${({ theme, isActive }) =>
        isActive ? `2px solid ${theme.blue}` : `1px solid ${theme.primary}10`};

    display: flex;
    align-items: center;
    cursor: pointer;
    transition: all 0.2s;

    &:hover {
        background: ${({ theme }) => `${theme.primary}05`};
    }
`

const NavigationLinkText = styled.span<IsActive>`
    font-size: 1.8rem;
    color: ${({ theme }) => theme.primary};
    opacity: ${({ isActive }) => (isActive ? 1 : 0.5)};
    margin-left: 1.4rem;
`

const LogOutLink = styled.span`
    cursor: pointer;

    font-size: 1.8rem;
    color: ${({ theme }) => theme.primary};
    opacity: 0.5;
    position: absolute;
    bottom: 2rem;
    left: 1.4rem;
    transition: transform 0.2s;

    &:hover {
        transform: scale(1.02);
    }
`

interface NavItem {
    name: CurrentFrameNameType
    label: string
    component: React.ReactNode
}

const SettingsPage: React.FC = () => {
    const { getSelf, getAllCaseworkers, logout } = useActions()
    const { user, organization } = useTypedSelector(
        (state) => state.caseworker.identity
    )
    const { caseworkers } = useTypedSelector((state) => state.caseworker.team)

    const authUser = useTypedSelector((state) => state.auth.user)
    const { isTrialExpired } = useTypedSelector((state) => state.payment)

    const isPartner = useIsPartner()
    const isCaseworker = useIsCaseworker()
    const isClient = authUser?.roles.includes('client') ?? false

    const translation = useTranslation()

    const navItems: NavItem[] = [
        {
            name: 'my-account',
            label: translation.settingsPartner.myAccount,
            component: <MyAccount />,
        },
        ...(isCaseworker
            ? [
                  {
                      name: 'auto-reply' as const,
                      label: translation.settingsPartner.automaticReply,
                      component: <AutoReply />,
                  },
              ]
            : []),
        ...(isPartner
            ? [
                  {
                      name: 'organization' as const,
                      label: translation.reusable.organization,
                      component: <Organization />,
                  },
                  {
                      name: 'users' as const,
                      label: translation.singleClientViewPartner.lineOfContact,
                      component: <PartnerUsers />,
                  },
              ]
            : []),
        ...(isClient
            ? [
                  {
                      name: 'payment-methods' as const,
                      label: translation.settingsPartner.billing,
                      component: <Payment />,
                  },
              ]
            : []),
    ]

    const defaultFrame = isTrialExpired ? 'payment-methods' : 'my-account'

    const [currentFrameName, setCurrentFrameName] =
        useState<CurrentFrameNameType>(defaultFrame)

    const currentNavItem = navItems.find(
        (item) => item.name === currentFrameName
    )

    useEffect(() => {
        !user && getSelf()
        if (isPartner && organization) {
            caseworkers.length === 0 && getAllCaseworkers(organization.id)
        }
        // eslint-disable-next-line
    }, [organization])

    return (
        <Container>
            <HeadingSecondary>
                {translation.settingsPartner.headlineSettings}
            </HeadingSecondary>
            <FlexContainer>
                <LeftContainer>
                    <NavigationContainer>
                        <Card>
                            {navItems.map((item) => (
                                <NavigationLink
                                    key={item.name}
                                    isActive={currentFrameName === item.name}
                                    onClick={() =>
                                        setCurrentFrameName(item.name)
                                    }
                                >
                                    <NavigationLinkText
                                        isActive={
                                            currentFrameName === item.name
                                        }
                                    >
                                        {item.label}
                                    </NavigationLinkText>
                                </NavigationLink>
                            ))}
                            <LogOutLink onClick={logout}>
                                {translation.settingsPartner.logOut}
                            </LogOutLink>
                        </Card>
                    </NavigationContainer>
                </LeftContainer>
                <RightContainer>{currentNavItem?.component}</RightContainer>
            </FlexContainer>
        </Container>
    )
}

export default SettingsPage
